import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { SidebarProvider } from '@/components/ui/sidebar';
import { DynamicSidebar } from './DynamicSidebar';
import { FiscalConfigWrapper } from './FiscalConfigWrapper';
import { Header } from './Header';
import { usePalette } from '@/hooks/usePalette';
import { MobileTabBar } from './mobile/MobileTabBar';
import { SwipeGestures } from './mobile/SwipeGestures';
import { PWAInstallPrompt } from './PWAInstallPrompt';
import { MobileRouter } from './mobile/MobileRouter';
import { useMobileView } from '@/hooks/useMobileDetection';
import { Footer } from './Footer';
import { UIDensityProvider } from '@/hooks/useUIDensity';

interface LayoutProps {
  children: React.ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { loadSavedPalette } = usePalette();
  const { shouldUseMobileView } = useMobileView();

  // Cargar paleta guardada al montar el layout
  React.useEffect(() => {
    loadSavedPalette();
  }, [loadSavedPalette]);

  // CRÍTICO: Verificar si estamos en modo password recovery
  // Detectamos desde la URL (search o hash) y activamos el flag antes de cualquier navegación
  const location = useLocation();
  const searchParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash;
  const hasRecoveryInUrl = searchParams.get('type') === 'recovery' || hash.includes('type=recovery');
  const forceRecoveryPath = location.pathname === '/reset-password';
  if (hasRecoveryInUrl || forceRecoveryPath) {
    localStorage.setItem('password_recovery_mode', 'true');
  }

  const isPasswordRecoveryMode = localStorage.getItem('password_recovery_mode') === 'true' || hasRecoveryInUrl || forceRecoveryPath;

  // Solo redirigimos a /reset-password si NO estamos ya en esa ruta.
  // Además, preservamos los tokens reenviando search y hash actuales.
  if (isPasswordRecoveryMode && location.pathname !== '/reset-password') {
    console.log('🔒 Modo recovery detectado en Layout - redirigiendo a reset-password');
    return <Navigate to={`/reset-password${location.search}${location.hash}`} replace />;
  }

  return (
    <UIDensityProvider>
      <SidebarProvider>
        <FiscalConfigWrapper>
          <div className="min-h-screen flex flex-col w-full max-w-full overflow-x-hidden bg-gradient-to-br from-background to-background/95 apple-transition">
          {/* Header Fijo */}
          <Header />
          
          {/* Layout Principal con espacio mejorado y overflow controlado */}
          <div className="flex flex-1 pt-12 sm:pt-14 gap-0 sm:gap-2 lg:gap-4 xl:gap-6 w-full max-w-full overflow-x-hidden">
            {/* Sidebar - Solo visible en desktop con variant floating */}
            {!shouldUseMobileView && (
              <div className="hidden md:block flex-shrink-0">
                <DynamicSidebar />
              </div>
            )}
            
            {/* Contenido principal con margin para separación visual y overflow controlado */}
            <div className="flex-1 flex flex-col min-w-0 max-w-full overflow-hidden">
              {shouldUseMobileView ? (
                // Experiencia móvil completa con router específico
                <MobileRouter>
                  <div className="safe-area-bottom min-h-0 flex-1 flex flex-col pb-16 w-full max-w-full overflow-x-hidden">
                    {children}
                  </div>
                </MobileRouter>
              ) : (
                // Experiencia desktop con gestos opcionales y margen
                <SwipeGestures className="flex-1 flex flex-col overflow-x-hidden max-w-full">
                  <main className="flex-1 overflow-y-auto overflow-x-hidden pwa-content apple-content pb-16 md:pb-20 w-full max-w-full">
                    <div className="w-full max-w-full pl-0 md:pl-2 lg:pl-4 overflow-x-hidden">
                      {children}
                    </div>
                  </main>
                </SwipeGestures>
              )}
            </div>
          </div>
          
          {/* Footer que cubre toda la pantalla */}
          <Footer />
          
          {/* Tab Bar móvil */}
          {shouldUseMobileView && <MobileTabBar />}
          
          {/* PWA Install Prompt */}
          <PWAInstallPrompt />
          </div>
        </FiscalConfigWrapper>
      </SidebarProvider>
    </UIDensityProvider>
  );
};